import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Link, withRouter } from 'react-router-dom'
import { Menu, Layout } from 'antd'
import classNames from 'classnames'
import store from 'store'
import PerfectScrollbar from 'react-perfect-scrollbar'
import style from './style.module.scss'
import {
  flattenMenuItems,
  mergeOpenKeys,
  normalizePath,
  resolveMenuSelection,
} from '../menuSelection.utils'
import { setMenuCollapsedPreference } from '../../../../redux/settings/menuPreference'

const mapStateToProps = ({ menu, settings, user }) => ({
  menuData: menu.menuData,
  isMenuCollapsed: settings.isMenuCollapsed,
  isMobileView: settings.isMobileView,
  isMenuUnfixed: settings.isMenuUnfixed,
  isMenuShadow: settings.isMenuShadow,
  leftMenuWidth: settings.leftMenuWidth,
  menuColor: settings.menuColor,
  logo: settings.logo,
  role: user.role,
})

// Tiempo máximo que se espera a que llegue la navegación iniciada desde el buscador
const REVEAL_MAX_WAIT_MS = 3000

const MenuLeft = ({
  dispatch,
  menuData = [],
  location: { pathname },

  isMenuCollapsed,
  isMobileView,
  isMenuUnfixed,
  isMenuShadow,
  leftMenuWidth,
  menuColor,
  logo,
  role,
}) => {
  const [selectedKeys, setSelectedKeys] = useState(store.get('app.menu.selectedKeys') || [])
  const [openedKeys, setOpenedKeys] = useState(store.get('app.menu.openedKeys') || [])
  const [routeKeyMenu, setRouteKeyMenu] = useState('')

  // Direct reference to the PerfectScrollbar inner scroll container
  const psContainerRef = useRef(null)
  // Opción elegida en el buscador superior mientras dura esa navegación (ver handleMenuReveal)
  const revealRef = useRef(null)
  const selectedKeysSignature = selectedKeys.join('|')

  useEffect(() => {
    applySelectedKeys()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menuData, role, routeKeyMenu])

  // El buscador superior avisa de la opción elegida antes de navegar: se deja abierto solo el
  // grupo de esa opción (sin acumular los de páginas anteriores) y se marca como seleccionada.
  useEffect(() => {
    const handleMenuReveal = event => {
      const key = event?.detail?.key
      if (!key) return
      const visibleMenu = menuData.filter(
        menuItem => !menuItem.roles || menuItem.roles.includes(role),
      )
      const target = flattenMenuItems(visibleMenu).find(item => item.key === key)
      if (!target) return

      // Hasta llegar a la página elegida, applySelectedKeys respeta esta opción: el router
      // todavía puede notificar el keyMenu de la ruta anterior y eso reabriría su grupo.
      revealRef.current = target.url
        ? {
            key: target.key,
            parentKeys: target.parentKeys,
            url: target.url,
            arrived: normalizePath(pathname) === target.url,
            expiresAt: Date.now() + REVEAL_MAX_WAIT_MS,
          }
        : null

      setSelectedKeys([target.key])
      setOpenedKeys(target.parentKeys)
      store.set('app.menu.selectedKeys', [target.key])
      store.set('app.menu.openedKeys', target.parentKeys)
    }
    window.addEventListener('app:menu-reveal', handleMenuReveal)
    return () => window.removeEventListener('app:menu-reveal', handleMenuReveal)
  }, [menuData, role, pathname])

  useEffect(() => {
    const handleRouteMenuKey = event => {
      const nextKey = event?.detail?.keyMenu || ''
      setRouteKeyMenu(nextKey)
    }
    window.addEventListener('app:route-key-menu', handleRouteMenuKey)
    return () => window.removeEventListener('app:route-key-menu', handleRouteMenuKey)
  }, [])

  useEffect(() => {
    const ANT_ANIMATION_MS = 400 // Ant Design inline menu animation is ~300ms; 400ms gives margin
    const POLL_INTERVAL_MS = 100
    const MAX_WAIT_MS = 8000

    // Runs on mount and every time the selected menu item changes (route change, search result,
    // etc.). The cleanup cancels a pending scroll if the selection changes again meanwhile.
    const startedAt = Date.now()
    let settleTimeoutId = null

    const scrollToSelected = () => {
      const psContainer = psContainerRef.current
      if (!psContainer) return

      const selectedItem = psContainer.querySelector('.ant-menu-item-selected')
      if (!selectedItem) return

      // After animation all heights are final — getBoundingClientRect is accurate.
      const containerRect = psContainer.getBoundingClientRect()
      const itemRect = selectedItem.getBoundingClientRect()

      // Item's top offset within the full scrollable content
      const itemTopInContent = itemRect.top - containerRect.top + psContainer.scrollTop
      const itemBottomInContent = itemTopInContent + selectedItem.offsetHeight

      const visibleTop = psContainer.scrollTop
      const visibleBottom = visibleTop + psContainer.clientHeight

      // Already fully visible — nothing to do
      if (itemTopInContent >= visibleTop && itemBottomInContent <= visibleBottom) {
        return
      }

      // Scroll so the item is vertically centered in the visible area
      const centeredScrollTop =
        itemTopInContent - psContainer.clientHeight / 2 + selectedItem.offsetHeight / 2
      psContainer.scrollTop = Math.max(0, centeredScrollTop)
    }

    // Step 1: poll until the selected item appears in DOM
    const waitForItemInterval = window.setInterval(() => {
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        window.clearInterval(waitForItemInterval)
        return
      }

      const psContainer = psContainerRef.current
      if (!psContainer) return

      const selectedItem = psContainer.querySelector('.ant-menu-item-selected')
      if (!selectedItem) return

      // Step 2: item found — stop polling and wait for Ant animation to settle
      window.clearInterval(waitForItemInterval)

      settleTimeoutId = window.setTimeout(scrollToSelected, ANT_ANIMATION_MS)
    }, POLL_INTERVAL_MS)

    return () => {
      window.clearInterval(waitForItemInterval)
      if (settleTimeoutId) window.clearTimeout(settleTimeoutId)
    }
  }, [selectedKeysSignature, isMenuCollapsed])

  const applySelectedKeys = () => {
    const reveal = revealRef.current
    if (reveal && Date.now() > reveal.expiresAt) {
      revealRef.current = null
    } else if (reveal) {
      if (normalizePath(pathname) === reveal.url) {
        reveal.arrived = true
        setSelectedKeys([reveal.key])
        setOpenedKeys(reveal.parentKeys)
        store.set('app.menu.selectedKeys', [reveal.key])
        store.set('app.menu.openedKeys', reveal.parentKeys)
        return
      }
      // Navegación del buscador todavía en curso: no pisar lo que ya se dejó puesto.
      if (!reveal.arrived) return
      // Ya se llegó a la página elegida y el usuario se ha ido a otra.
      revealRef.current = null
    }

    const visibleMenu = menuData.filter(menuItem => !menuItem.roles || menuItem.roles.includes(role))
    const { selectedKey, ancestorKeys } = resolveMenuSelection({
      menuData: visibleMenu,
      pathname,
      routeKeyMenu,
    })

    const nextSelectedKeys = selectedKey ? [selectedKey] : []
    const persistedOpenedKeys = store.get('app.menu.openedKeys') || []
    const nextOpenedKeys = mergeOpenKeys(persistedOpenedKeys, ancestorKeys)

    setSelectedKeys(nextSelectedKeys)
    setOpenedKeys(nextOpenedKeys)

    store.set('app.menu.selectedKeys', nextSelectedKeys)
    store.set('app.menu.openedKeys', nextOpenedKeys)
  }

  const onCollapse = (value, type) => {
    if (type === 'responsive' && isMenuCollapsed) {
      return
    }
    // Solo la elección del usuario (trigger del menú) se recuerda; el colapso automático por
    // ancho de ventana ('responsive') no pisa su preferencia.
    if (type !== 'responsive') {
      setMenuCollapsedPreference(!isMenuCollapsed)
    }
    dispatch({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'isMenuCollapsed',
        value: !isMenuCollapsed,
      },
    })
    // No vaciar openedKeys: con el menú colapsado ya se pasa openKeys=[] al <Menu> (ver abajo) y
    // al expandirlo de nuevo hay que recuperar los grupos abiertos de la opción seleccionada.
  }

  const onOpenChange = keys => {
    // Antd llama a onOpenChange([]) al colapsar el menú; ignorarlo para no perder los grupos
    // abiertos que hay que recuperar al expandirlo de nuevo.
    if (isMenuCollapsed && !isMobileView) return
    store.set('app.menu.openedKeys', keys)
    setOpenedKeys(keys)
  }

  const handleClick = e => {
    revealRef.current = null
    store.set('app.menu.selectedKeys', [e.key])
    setSelectedKeys([e.key])
  }

  const generateMenuItems = () => {
    const generateItem = item => {
      const { key, title, url, icon, disabled, count } = item
      if (item.category) {
        return {
          type: 'group',
          key: key || Math.random().toString(),
          label: item.title,
        }
      }
      if (item.url) {
        return {
          key,
          disabled,
          icon: icon ? <span className={`${icon} ${style.icon} icon-collapsed-hidden`} /> : null,
          label: item.target ? (
            <a href={url} target={item.target} rel="noopener noreferrer">
              <span className={style.title}>{title}</span>
              {count && <span className="badge badge-success ml-2">{count}</span>}
            </a>
          ) : (
            <Link to={url}>
              <span className={style.title}>{title}</span>
              {count && <span className="badge badge-success ml-2">{count}</span>}
            </Link>
          ),
        }
      }
      return {
        key,
        disabled,
        icon: icon ? <span className={`${icon} ${style.icon} icon-collapsed-hidden`} /> : null,
        label: (
          <span className={style.title}>
            {title}
            {count && <span className="badge badge-success ml-2">{count}</span>}
          </span>
        ),
      }
    }

    const generateSubmenu = items =>
      items
        .filter(menuItem => !menuItem.roles || menuItem.roles.includes(role))
        .map(menuItem => {
          if (menuItem.children) {
            return {
              key: menuItem.key,
              icon: menuItem.icon ? <span className={`${menuItem.icon} ${style.icon}`} /> : null,
              label: (
                <span>
                  <span className={style.title}>{menuItem.title}</span>
                  {menuItem.count && (
                    <span className="badge badge-success ml-2">{menuItem.count}</span>
                  )}
                </span>
              ),
              children: generateSubmenu(menuItem.children),
            }
          }
          return generateItem(menuItem)
        })

    return menuData
      .filter(menuItem => !menuItem.roles || menuItem.roles.includes(role))
      .map(menuItem => {
        if (menuItem.children) {
          return {
            key: menuItem.key,
            icon: menuItem.icon ? <span className={`${menuItem.icon} ${style.icon}`} /> : null,
            label: (
              <span>
                <span className={style.title}>{menuItem.title}</span>
                {menuItem.count && (
                  <span className="badge badge-success ml-2">{menuItem.count}</span>
                )}
              </span>
            ),
            children: generateSubmenu(menuItem.children),
          }
        }
        return generateItem(menuItem)
      })
  }

  const menuSettings = isMobileView
    ? {
        width: leftMenuWidth,
        collapsible: false,
        collapsed: false,
        onCollapse,
      }
    : {
        width: leftMenuWidth,
        collapsible: true,
        collapsed: isMenuCollapsed,
        onCollapse,
        breakpoint: 'lg',
      }

  return (
    <Layout.Sider
      {...menuSettings}
      className={classNames(`${style.menu}`, {
        [style.white]: menuColor === 'white',
        [style.gray]: menuColor === 'gray',
        [style.dark]: menuColor === 'dark',
        [style.unfixed]: isMenuUnfixed,
        [style.shadow]: isMenuShadow,
      })}
    >
      <div
        className={style.menuOuter}
        style={{
          width: isMenuCollapsed && !isMobileView ? 80 : leftMenuWidth,
          height: isMobileView || isMenuUnfixed ? 'calc(100% - 64px)' : 'calc(100% - 110px)',
        }}
      >
        <div className={style.logoContainer}>
          <div className={style.logo}>
            <img
              src="/resources/images/ro_icon.png"
              width="24"
              height="24"
              className="mr-2"
              alt="Regalo Original"
            />
            <div className={style.name}>
              <img
                src="/resources/images/ro_logo.png"
                className="mr-2"
                alt="Regalo Original"
                width="140"
                height="35"
              />
            </div>
            {logo === '' && <div className={style.descr}>React</div>}
          </div>
        </div>
        <PerfectScrollbar containerRef={ref => { psContainerRef.current = ref }}>
          <Menu
            items={generateMenuItems()}
            onClick={handleClick}
            selectedKeys={selectedKeys}
            openKeys={isMenuCollapsed && !isMobileView ? [] : openedKeys}
            onOpenChange={onOpenChange}
            mode="inline"
            className={style.navigation}
            inlineIndent="15"
          />
        </PerfectScrollbar>
      </div>
    </Layout.Sider>
  )
}

export default withRouter(connect(mapStateToProps)(MenuLeft))
